import * as pdfjsLib from 'pdfjs-dist';
import { ParseError } from '../types.js';
let workerConfigured = false;
/**
 * Set the URL of pdf.js's web worker. Must be called once before `extractPdfTier1`.
 *
 * In a Vite app:
 *   import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
 *   configurePdfWorker(workerUrl);
 */
export function configurePdfWorker(workerSrc) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;
    workerConfigured = true;
}
/**
 * Tier 1: extract text from PDF using pdf.js, group items into lines by y-coordinate,
 * and join with newlines. Per-page sections separated by `## Page N` headers.
 *
 * Best for: text-heavy lecture slides, modern case studies, single-column layouts.
 * Falls down on: multi-column papers, scanned PDFs (no text layer), heavy tables.
 *
 * The `warnings` array carries soft signals like "page 3: very low text density"
 * that the UI can surface so the user knows to manually inspect / fix the markdown.
 */
export async function extractPdfTier1(file) {
    if (!workerConfigured) {
        throw new ParseError('pdf.js worker not configured — call configurePdfWorker() first');
    }
    const start = Date.now();
    const warnings = [];
    let buffer;
    try {
        buffer = await file.arrayBuffer();
    }
    catch (err) {
        throw new ParseError('failed to read file as ArrayBuffer', err);
    }
    let pdf;
    try {
        pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
    }
    catch (err) {
        throw new ParseError('pdf.js failed to load the document', err);
    }
    const sections = [];
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        const items = textContent.items.filter(isTextItem);
        const totalChars = items.reduce((acc, it) => acc + it.str.length, 0);
        if (totalChars < 50) {
            warnings.push(`page ${pageNum}: low text density (${totalChars} chars) — may be a scanned image`);
        }
        const lines = groupIntoLines(items);
        const text = lines
            .map((line) => line.map((it) => it.str).join(' ').replace(/\s+/g, ' ').trim())
            .filter((s) => s.length > 0)
            .join('\n');
        sections.push(`## Page ${pageNum}\n\n${text}`);
    }
    const markdown = sections.join('\n\n');
    return {
        markdown,
        meta: {
            sourceFormat: 'pdf',
            tier: 'tier1',
            pageCount: pdf.numPages,
            durationMs: Date.now() - start,
            warnings,
        },
    };
}
/** pdf.js TextContent items are a union of TextItem and TextMarkedContent. We only want the former. */
function isTextItem(item) {
    return 'str' in item;
}
/**
 * Bucket text items by their y-coordinate (transform[5], rounded to 1px),
 * then within a bucket sort by x. PDF y origin is bottom-left, so iterate buckets
 * top-to-bottom by sorting y descending.
 *
 * Limitation: multi-column layouts with same y on different columns merge incorrectly.
 * Acceptable for Tier 1; column-detection is a Tier 2 feature.
 */
function groupIntoLines(items) {
    const buckets = new Map();
    for (const item of items) {
        const transform = item.transform;
        if (!Array.isArray(transform) || transform.length < 6)
            continue;
        const yRaw = transform[5];
        if (typeof yRaw !== 'number')
            continue;
        const y = Math.round(yRaw);
        let bucket = buckets.get(y);
        if (!bucket) {
            bucket = [];
            buckets.set(y, bucket);
        }
        bucket.push(item);
    }
    const sortedKeys = [...buckets.keys()].sort((a, b) => b - a);
    return sortedKeys.map((key) => {
        const line = buckets.get(key);
        return line.sort((a, b) => xOf(a) - xOf(b));
    });
}
function xOf(item) {
    const transform = item.transform;
    if (!Array.isArray(transform) || transform.length < 5)
        return 0;
    const x = transform[4];
    return typeof x === 'number' ? x : 0;
}
//# sourceMappingURL=tier1-text.js.map